/**
 * Sticky bar bottom-of-viewport con riepilogo preventivo: area totale, costo,
 * prezzo netto e margine. Sempre visibile durante lo scroll della pagina di
 * gestione preventivo per dare contesto al cartongessista mentre modifica
 * stratigrafie/quantità.
 *
 * Vista vendita (prezzo+margine) visibile solo a owner/admin coerentemente
 * con SellingPriceCard. Per gli altri ruoli mostra solo cost + area.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Calculator, TrendingUp } from 'lucide-react';
import { type CostBase } from '@/utils/pricing/applyPricing';
import { computeEstimateCostBase, rowCostOptionsFrom } from '@/utils/pricing/computeEstimateCostBase';
import { applyPricingForRows } from '@/utils/pricing/applyPricingForRows';
import { mergeEstimatePricing, type EstimatePricingOverrides } from '@/utils/pricing/mergeEstimatePricing';
import { useOrgProfile } from '@/hooks/useOrgSettings';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import type { EstimateStratigraphy } from '@/types/estimateStratigraphy';
import { formatNumber, formatEuroRounded, formatArea } from '@/utils/format';

interface EstimateStickySummaryBarProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stratigraphies: (EstimateStratigraphy & { stratigraphy?: any })[];
  totalCost: number;
  /** Override pricing del preventivo (markup/spese/IVA/sconto). Coerenza con SellingPriceCard + PDF. */
  pricingOverrides?: EstimatePricingOverrides | null;
}

function euro(v: number): string {
  return formatEuroRounded(v);
}

function num(v: unknown, fb = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

const EstimateStickySummaryBar: React.FC<EstimateStickySummaryBarProps> = ({
  stratigraphies,
  totalCost,
  pricingOverrides,
}) => {
  const { currentRole } = useCurrentOrganization();
  const canSeeMargins = currentRole === 'owner' || currentRole === 'admin';
  const { data: org } = useOrgProfile();
  const effective = useMemo(() => mergeEstimatePricing(org ?? null, pricingOverrides), [org, pricingOverrides]);
  const [collapsed, setCollapsed] = useState(false);

  const costBase = useMemo<CostBase>(
    () => computeEstimateCostBase(stratigraphies, rowCostOptionsFrom(effective)),
    [stratigraphies, effective],
  );

  const breakdown = useMemo(
    () => applyPricingForRows(costBase, effective, stratigraphies),
    [costBase, effective, stratigraphies],
  );

  if (stratigraphies.length === 0) return null;

  // Stessi campi della card: il margine si calcola in applyPricing, qui si legge.
  const { marginNet, marginNetPct, marginGross, marginGrossPct } = breakdown;

  return (
    <div className="sticky bottom-0 left-0 right-0 z-30 -mx-6 md:-mx-8 mt-6 border-t bg-background/95 backdrop-blur-sm shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)]">
      <div className="px-6 md:px-8 py-2.5 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5 text-sm">
          <Calculator className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Riepilogo:</span>
        </div>

        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Stratigrafie</span>
          <span className="font-semibold tabular-nums">{stratigraphies.length}</span>
        </div>

        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Area</span>
          <span className="font-semibold tabular-nums">{formatArea(costBase.totalArea)}</span>
        </div>

        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-muted-foreground">Costo</span>
          <span className="font-semibold tabular-nums">{euro(totalCost)}</span>
        </div>

        {canSeeMargins && !collapsed && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-muted-foreground">Prezzo netto</span>
              <span className="font-semibold tabular-nums text-primary">{euro(breakdown.netPrice)}</span>
            </div>

            <div
              className="flex items-center gap-1.5 text-sm"
              title={`Margine netto sul ricavo. Lordo ${euro(marginGross)} (${formatNumber(marginGrossPct, 1)}%) meno i costi accessori.`}
            >
              <span className="text-muted-foreground">Margine netto</span>
              <span className="font-semibold tabular-nums text-emerald-600 inline-flex items-center gap-1">
                <TrendingUp className="h-3.5 w-3.5" />
                {euro(marginNet)}
                <span className="text-xs font-normal text-muted-foreground">({formatNumber(marginNetPct, 1)}%)</span>
              </span>
            </div>
          </>
        )}

        {canSeeMargins && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-7 px-2 text-xs gap-1"
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Mostra margine' : 'Nascondi margine'}
          >
            {collapsed ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {collapsed ? 'Margine' : 'Compatto'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EstimateStickySummaryBar;
